import { Button, Drawer } from 'antd';
import styled from 'styled-components';
import { BlueColors, MediaQuery, NeutralColors } from 'theme';

interface ButtonTabProps {
  selected?: boolean;
  tabCount?: number;
}

interface PageTitleWrapperProps {
  hasBorderBottom: boolean;
  fullBleed?: boolean;
}

const PageTitleWrapper = styled.div<PageTitleWrapperProps>`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 16px;
  padding: 8px 0;
  margin-bottom: 8px;
  border-bottom: ${(props) =>
    props.hasBorderBottom ? `1px solid ${NeutralColors[20]}` : 'none'};

  @media (min-width: ${MediaQuery.screenSizesCss.tablet}) {
    gap: 24px;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  @media (min-width: ${MediaQuery.screenSizesCss.desktop}) {
    padding: ${(props) => (props.fullBleed ? '16px 56px 6px' : '8px 0')};
  }
`;

const TitleWrapper = styled.div`
  flex-grow: 1;
  display: flex;
  gap: 16px;
  align-items: center;
  flex-wrap: wrap;

  h2 {
    width: 100%;
    margin: 0 16px 0 0;
    display: flex;
    flex-wrap: wrap;

    @media (min-width: ${MediaQuery.screenSizesCss.tablet}) {
      width: auto;
    }
  }
`;

const ComplementaryTitle = styled.span`
  color: ${NeutralColors[60]};
  margin-left: 20px;
  display: flex;
  align-items: center;
`;

const ButtonsWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  max-width: 100%;
  flex-wrap: wrap-reverse;
  justify-content: flex-end;
  gap: 8px;
  flex-grow: 1;

  @media (min-width: ${MediaQuery.screenSizesCss.tablet}) {
    justify-self: flex-end;
    flex-wrap: nowrap;
  }
`;

const InlineButtonsWrapper = styled.div`
  display: flex;
  flex-shrink: 0;
  gap: 8px;
  margin-left: auto;
`;

const Description = styled.h4`
  margin-top: -16px;
  margin-bottom: 16px;
`;

const ContainerLeftIcon = styled.div`
  margin-right: 8px;
`;

const HeaderRow = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  width: 100%;

  @media (min-width: ${MediaQuery.screenSizesCss.tablet}) {
    width: auto;
    flex-grow: 1;
  }
`;

const MobileMenuButton = styled(Button)`
  display: flex;
  align-items: center;
  justify-content: center;
  margin-left: 8px;
  width: 40px !important;
  height: 40px !important;
  min-width: 40px;
  border-radius: 40px;
  border: 1px solid ${NeutralColors[30]};
  background-color: ${NeutralColors[0]} !important;

  svg {
    fill: ${NeutralColors[50]};
    color: ${NeutralColors[50]};
  }

  @media (min-width: ${MediaQuery.screenSizesCss.tablet}) {
    display: none;
  }
`;

const MobileTabsWrapper = styled.div`
  display: flex;
  overflow-x: auto;
  white-space: nowrap;
  width: 100%;
  padding-bottom: 4px;

  /* Hide scrollbar for Chrome, Safari and Opera */
  &::-webkit-scrollbar {
    display: none;
  }

  /* Hide scrollbar for IE, Edge and Firefox */
  -ms-overflow-style: none;
  scrollbar-width: none;
`;

const getMobileTabWidth = (count?: number) => {
  if (!count) return 'auto';
  if (count === 1) return '100%';
  if (count === 2) return '50%';
  return '40%';
};

const ButtonTab = styled.button<ButtonTabProps>`
  background: none;
  font-family: inherit;
  color: ${(props) => (props.selected ? BlueColors[60] : NeutralColors[80])};
  border: none;
  border-bottom: ${(props) =>
    props.selected
      ? `2px solid ${BlueColors[60]}`
      : `2px solid ${NeutralColors[20]}`};
  cursor: pointer;
  font-size: 16px;
  margin: 16px 0 0 0 !important;
  padding: 0 0 8px 0;
  font-weight: ${(props) => (props.selected ? '600' : '400')};

  flex-shrink: 0;
  text-align: center;
  width: ${(props) => getMobileTabWidth(props.tabCount)};

  :hover {
    color: ${BlueColors[60]};
  }

  @media (min-width: ${MediaQuery.screenSizesCss.tablet}) {
    margin: 0 16px -20px 0 !important;
    padding: 0 8px 16px 8px;
    width: auto;
    text-align: left;
    border-bottom: ${(props) =>
      props.selected ? `2px solid ${BlueColors[60]}` : `2px solid transparent`};
  }
`;

const BottomDrawer = styled(Drawer)`
  .fms-drawer-content-wrapper {
    box-shadow: none;
  }
  .fms-drawer-content {
    border-radius: 12px 12px 0 0 !important;
  }
  .fms-drawer-body {
    padding: 12px 16px 24px 16px;
  }
`;

const DrawerPill = styled.div`
  background-color: ${NeutralColors[20]};
  height: 4px;
  border-radius: 45px;
  width: 40px;
  margin: 12px auto 16px auto;
  flex-shrink: 0;
`;

export {
  BottomDrawer,
  ButtonsWrapper,
  ButtonTab,
  ComplementaryTitle,
  ContainerLeftIcon,
  Description,
  DrawerPill,
  HeaderRow,
  InlineButtonsWrapper,
  MobileMenuButton,
  MobileTabsWrapper,
  PageTitleWrapper,
  TitleWrapper,
};
